import React, { useEffect, useMemo, useState } from 'react'
import {
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Space,
  Button,
  Table,
  Tabs,
  Tag,
  message,
  Spin,
  Empty,
  Select,
  Modal
} from 'antd'
import {
  ReloadOutlined,
  TeamOutlined,
  UserOutlined,
  DashboardOutlined,
  FileTextOutlined,
  DollarOutlined,
  HistoryOutlined,
  PictureOutlined,
  VideoCameraOutlined
} from '@ant-design/icons'
import { agentPortalService, AgentPortalOverviewResponse } from '@/services/agentPortal'

const { Title, Text } = Typography

interface TableState<T> {
  items: T[]
  loading: boolean
  page: number
  limit: number
  total: number
}

const defaultTableState = <T,>(overrides?: Partial<TableState<T>>): TableState<T> => ({
  items: [],
  loading: false,
  page: 1,
  limit: 10,
  total: 0,
  ...overrides
})

const AgentPortalPage: React.FC = () => {
  const [overview, setOverview] = useState<AgentPortalOverviewResponse | null>(null)
  const [loadingOverview, setLoadingOverview] = useState(false)

  const [domains, setDomains] = useState<any[]>([])
  const [loadingDomains, setLoadingDomains] = useState(false)

  const [usersState, setUsersState] = useState<TableState<any>>(defaultTableState())
  const [generationsState, setGenerationsState] = useState<TableState<any>>(defaultTableState())
  const [paymentsState, setPaymentsState] = useState<TableState<any>>({ ...defaultTableState(), total: 0 })
  const [pointsState, setPointsState] = useState<TableState<any>>(defaultTableState())
  const [generationsType, setGenerationsType] = useState<'all' | 'image' | 'video'>('all')

  const [activeTab, setActiveTab] = useState<string>('domains')
  const [previewModal, setPreviewModal] = useState<{
    visible: boolean
    type: 'image' | 'video'
    prompt: string
    urls: string[]
    url?: string
  }>({
    visible: false,
    type: 'image',
    prompt: '',
    urls: [],
    url: ''
  })
  const [downloadLoading, setDownloadLoading] = useState(false)

  const loadOverview = async () => {
    try {
      setLoadingOverview(true)
      const response = await agentPortalService.getOverview()
      if (response.success && response.data) {
        setOverview(response.data)
      } else {
        throw new Error(response.message || '无法获取代理商概览')
      }
    } catch (error: any) {
      message.error(error.message || '获取代理商概览失败')
    } finally {
      setLoadingOverview(false)
    }
  }

  const loadDomains = async () => {
    try {
      setLoadingDomains(true)
      const response = await agentPortalService.getDomains()
      if (response.success) {
        setDomains(response.data || [])
      } else {
        throw new Error(response.message || '无法获取域名授权列表')
      }
    } catch (error: any) {
      message.error(error.message || '获取域名授权失败')
    } finally {
      setLoadingDomains(false)
    }
  }

  const loadUsers = async (page = usersState.page, limit = usersState.limit) => {
    try {
      setUsersState((prev) => ({ ...prev, loading: true }))
      const response = await agentPortalService.getUsers({ page, limit })
      if (response.success && response.data) {
        setUsersState({
          items: response.data.items || [],
          loading: false,
          page: response.data.pagination?.page || page,
          limit: response.data.pagination?.limit || limit,
          total: response.data.pagination?.total || 0
        })
      } else {
        throw new Error(response.message || '无法获取代理商用户列表')
      }
    } catch (error: any) {
      setUsersState((prev) => ({ ...prev, loading: false }))
      message.error(error.message || '获取代理商用户失败')
    }
  }

  const loadGenerations = async (page = generationsState.page, limit = generationsState.limit, type = generationsType) => {
    try {
      setGenerationsState((prev) => ({ ...prev, loading: true }))
      const response = await agentPortalService.getGenerations({ page, limit, type })
      if (response.success && response.data) {
        setGenerationsState({
          items: response.data.items || [],
          loading: false,
          page: response.data.pagination?.page || page,
          limit: response.data.pagination?.limit || limit,
          total: response.data.pagination?.total || 0
        })
      } else {
        throw new Error(response.message || '无法获取生成记录')
      }
    } catch (error: any) {
      setGenerationsState((prev) => ({ ...prev, loading: false }))
      message.error(error.message || '获取生成记录失败')
    }
  }

  const loadPayments = async (page = paymentsState.page, limit = paymentsState.limit) => {
    try {
      setPaymentsState((prev) => ({ ...prev, loading: true }))
      const response = await agentPortalService.getPayments({ page, limit })
      if (response.success && response.data) {
        setPaymentsState({
          items: response.data.items || [],
          loading: false,
          page: response.data.pagination?.page || page,
          limit: response.data.pagination?.limit || limit,
          total: response.data.pagination?.total || 0
        })
      } else {
        throw new Error(response.message || '无法获取充值记录')
      }
    } catch (error: any) {
      setPaymentsState((prev) => ({ ...prev, loading: false }))
      message.error(error.message || '获取充值记录失败')
    }
  }

  const loadPointsLogs = async (page = pointsState.page, limit = pointsState.limit) => {
    try {
      setPointsState((prev) => ({ ...prev, loading: true }))
      const response = await agentPortalService.getPointsLogs({ page, limit })
      if (response.success && response.data) {
        setPointsState({
          items: response.data.items || [],
          loading: false,
          page: response.data.pagination?.page || page,
          limit: response.data.pagination?.limit || limit,
          total: response.data.pagination?.total || 0
        })
      } else {
        throw new Error(response.message || '无法获取积分日志')
      }
    } catch (error: any) {
      setPointsState((prev) => ({ ...prev, loading: false }))
      message.error(error.message || '获取积分日志失败')
    }
  }

  const extractPreviewData = (record: any) => {
    const type = (record.recordType || record.type || '').toLowerCase() === 'video' ? 'video' : 'image'

    const collectImages = (value: any): string[] => {
      if (!value) return []
      if (typeof value === 'string') {
        if (value.trim().startsWith('[') || value.trim().startsWith('{')) {
          try {
            return collectImages(JSON.parse(value))
          } catch {
            return value.startsWith('http') ? [value] : []
          }
        }
        return value.startsWith('http') ? [value] : []
      }
      if (Array.isArray(value)) {
        return value.flatMap(collectImages).filter((url) => typeof url === 'string')
      }
      if (typeof value === 'object') {
        if (value.url) return collectImages(value.url)
        if (value.imageUrl) return collectImages(value.imageUrl)
        if (Array.isArray(value.urls)) return collectImages(value.urls)
      }
      return []
    }

    const collectVideo = (value: any): string | undefined => {
      if (!value) return undefined
      if (typeof value === 'string') {
        if (value.trim().startsWith('[') || value.trim().startsWith('{')) {
          try {
            const parsed = JSON.parse(value)
            return collectVideo(parsed)
          } catch {
            return value.startsWith('http') ? value : undefined
          }
        }
        return value.startsWith('http') ? value : undefined
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          const url = collectVideo(item)
          if (url) return url
        }
      } else if (typeof value === 'object') {
        return collectVideo(
          value.videoUrl ||
          value.url ||
          value.outputVideo ||
          value.outputVideoUrl ||
          value.output
        )
      }
      return undefined
    }

    if (type === 'video') {
      const videoUrl = collectVideo(
        record.outputVideo ||
        record.outputVideoUrl ||
        record.outputVideoLink ||
        record.videoUrl ||
        record.outputs ||
        record.output
      )
      return {
        hasPreview: !!videoUrl,
        type: 'video' as const,
        urls: [] as string[],
        url: videoUrl
      }
    }

    const urls = collectImages(
      record.outputImages ||
      record.images ||
      record.outputs ||
      record.resultImages ||
      record.output
    )

    return {
      hasPreview: urls.length > 0,
      type: 'image' as const,
      urls,
      url: undefined
    }
  }

  const openPreview = (record: any) => {
    const previewInfo = extractPreviewData(record)
    if (!previewInfo.hasPreview) {
      message.warning('当前记录暂无可预览的文件')
      return
    }
    setPreviewModal({
      visible: true,
      type: previewInfo.type,
      prompt: record.prompt || record.description || '',
      urls: previewInfo.urls || [],
      url: previewInfo.url
    })
  }

  const handleDownload = () => {
    if (previewModal.type === 'video' && previewModal.url) {
      triggerDownload(previewModal.url)
      return
    }
    if (previewModal.urls.length > 0) {
      setDownloadLoading(true)
      const first = previewModal.urls[0]
      triggerDownload(first).finally(() => setDownloadLoading(false))
      return
    }
    message.warning('暂无可下载的文件')
  }

  const triggerDownload = async (url: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = url.split('/').pop() || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
      message.success('下载已开始')
    } catch (error) {
      console.error(error)
      message.error('下载失败，请稍后重试')
    }
  }

  useEffect(() => {
    loadOverview()
    loadDomains()
    loadUsers()
    loadGenerations()
    loadPayments()
    loadPointsLogs()
  }, [])

  useEffect(() => {
    if (activeTab === 'generations') {
      loadGenerations(1, generationsState.limit, generationsType)
    }
  }, [generationsType])

  const overviewCards = useMemo(() => {
    if (!overview) return null
    const statusColor = overview.agent.status === 'ACTIVE'
      ? '#22c55e'
      : overview.agent.status === 'SUSPENDED'
        ? '#f97316'
        : '#94a3b8'

    return (
      <div className="overflow-hidden bg-white shadow-[0_20px_55px_rgba(15,23,42,0.08)] border border-gray-100 unified-card" style={{
        borderRadius: 'var(--radius-xl)',
        marginBottom: 'var(--spacing-lg)',
        padding: 'var(--spacing-lg)'
      }}>
          <Row gutter={[24, 24]} align="middle">
            <Col xs={24} lg={10}>
              <Space direction="vertical" size={16}>
                <Space align="center">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-100 via-indigo-100 to-purple-100 flex items-center justify-center" style={{
                    borderRadius: 'var(--radius-xl)'
                  }}>
                    <TeamOutlined style={{ fontSize: 'var(--font-h4-mobile)' }} className="text-indigo-500" />
                  </div>
                  <div>
                    <Title level={4} style={{ marginBottom: 4 }}>{overview.agent.name}</Title>
                    <Text type="secondary">代理商编号：{overview.agent.code}</Text>
                  </div>
                </Space>
                <Space wrap>
                  <Tag color={statusColor} style={{ borderRadius: 999, padding: '2px 12px', color: '#fff', border: 'none' }}>
                    {overview.agent.status === 'ACTIVE' ? '已激活' : overview.agent.status === 'SUSPENDED' ? '已暂停' : '已过期'}
                  </Tag>
                  {overview.agent.expiresAt && (
                    <Tag color="blue" style={{ borderRadius: 999, padding: '2px 12px', color: '#fff', border: 'none' }}>
                      到期：{new Date(overview.agent.expiresAt).toLocaleDateString('zh-CN')}
                    </Tag>
                  )}
                  {typeof overview.agent.maxDomains === 'number' && (
                    <Tag
                      style={{
                        borderRadius: 999,
                        padding: '2px 12px',
                        background: '#ede9fe',
                        color: '#5b21b6',
                        border: 'none'
                      }}
                    >
                      域名额度：{overview.agent.maxDomains === 0 ? '不限' : `${overview.agent.maxDomains} 个`}
                    </Tag>
                  )}
                </Space>
                <Text style={{ color: '#64748b' }}>
                  欢迎来到代理商门户，这里可以统一查看授权域名、下属用户、创作记录以及充值消费情况。您也可以在下方表格中刷新数据或导出记录。
                </Text>
              </Space>
            </Col>
            <Col xs={24} lg={14}>
              <Row gutter={[16, 16]}>
                <Col xs={12} md={8}>
                  <Statistic
                    title="授权域名"
                    value={overview.stats.domains}
                    prefix={<DashboardOutlined className="text-blue-500" />}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic
                    title="下属用户"
                    value={overview.stats.users}
                    prefix={<UserOutlined className="text-green-500" />}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic
                    title="充值总额(元)"
                    value={overview.stats.totalPayments}
                    precision={2}
                    prefix={<DollarOutlined className="text-amber-500" />}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic
                    title="图片生成"
                    value={overview.stats.imageGenerations}
                    prefix={<PictureOutlined className="text-purple-500" />}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic
                    title="视频生成"
                    value={overview.stats.videoGenerations}
                    prefix={<VideoCameraOutlined className="text-pink-500" />}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic
                    title="生成总数"
                    value={overview.stats.totalGenerations}
                    prefix={<HistoryOutlined className="text-slate-500" />}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
      </div>
    )
  }, [overview])

  const domainColumns = [
    {
      title: '域名',
      dataIndex: 'fullDomain',
      key: 'fullDomain',
      render: (value: string, record: any) => (
        <Space direction="vertical" size={0}>
          <Text strong>{value}</Text>
          {record.subdomain && <Text type="secondary">子域名：{record.subdomain}</Text>}
        </Space>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'success' : status === 'SUSPENDED' ? 'warning' : 'default'}>
          {status === 'ACTIVE' ? '已激活' : status === 'SUSPENDED' ? '已暂停' : '已过期'}
        </Tag>
      )
    },
    {
      title: '授权密钥',
      dataIndex: 'licenseKey',
      key: 'licenseKey',
      width: 260,
      ellipsis: true
    },
    {
      title: '到期时间',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      width: 160,
      render: (value: string | null) => value ? new Date(value).toLocaleString('zh-CN') : '永久'
    },
    {
      title: '最后验证',
      dataIndex: 'lastVerifiedAt',
      key: 'lastVerifiedAt',
      width: 180,
      render: (value: string | null) => value ? new Date(value).toLocaleString('zh-CN') : '—'
    }
  ]

  const userColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      render: (value: string, record: any) => (
        <Space direction="vertical" size={0}>
          <Text strong>{value || record.email}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
        </Space>
      )
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role: string) => (
        <Tag color={role === 'AGENT_ADMIN' ? 'purple' : role === 'ADMIN' ? 'volcano' : 'blue'}>
          {role}
        </Tag>
      )
    },
    {
      title: '积分',
      dataIndex: 'credits',
      key: 'credits',
      width: 80
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (value: boolean) => (
        <Tag color={value ? 'success' : 'error'}>
          {value ? '激活' : '禁用'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (value: string) => new Date(value).toLocaleString('zh-CN')
    }
  ]

  const generationColumns = [
    {
      title: '类型',
      dataIndex: 'recordType',
      key: 'recordType',
      width: 90,
      render: (_: string, record: any) => {
        const type = record.recordType || record.type
        if (type === 'video') {
          return <Tag color="magenta">视频</Tag>
        }
        return <Tag color="geekblue">图片</Tag>
      }
    },
    {
      title: '模型',
      dataIndex: 'model',
      key: 'model',
      width: 180,
      ellipsis: true
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={
          status === 'COMPLETED' ? 'success'
          : status === 'FAILED' ? 'error'
          : status === 'PROCESSING' ? 'warning'
          : 'default'}>
          {status}
        </Tag>
      )
    },
    {
      title: '提示词',
      dataIndex: 'prompt',
      key: 'prompt',
      ellipsis: true
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value: string) => new Date(value).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_: any, record: any) => {
        const { hasPreview } = extractPreviewData(record)
        return (
          <Button
            type="link"
            size="small"
            disabled={!hasPreview}
            onClick={() => openPreview(record)}
          >
            预览
          </Button>
        )
      }
    }
  ]

  const paymentColumns = [
    {
      title: '订单号',
      dataIndex: 'orderId',
      key: 'orderId',
      ellipsis: true
    },
    {
      title: '金额 (元)',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (value: number) => value?.toFixed(2)
    },
    {
      title: '积分',
      dataIndex: 'credits',
      key: 'credits',
      width: 100
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={status === 'COMPLETED' ? 'success' : status === 'PENDING' ? 'processing' : 'default'}>
          {status}
        </Tag>
      )
    },
    {
      title: '支付方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 140
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value: string) => new Date(value).toLocaleString('zh-CN')
    }
  ]

  const pointsColumns = [
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 120
    },
    {
      title: '变动额度',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (value: number) => (
        <span className={value >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
          {value >= 0 ? '+' : ''}{value}
        </span>
      )
    },
    {
      title: '余额',
      dataIndex: 'balance',
      key: 'balance',
      width: 120
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 160
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: '时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value: string) => new Date(value).toLocaleString('zh-CN')
    }
  ]

  const tabsItems = [
    {
      key: 'domains',
      label: '域名授权',
      children: (
        <Card variant="borderless" className="shadow-sm">
          <Space style={{ marginBottom: 'var(--spacing-md)' }}>
            <Button icon={<ReloadOutlined />} onClick={loadDomains} loading={loadingDomains}>
              刷新
            </Button>
            <Text type="secondary">共 {domains.length} 条授权</Text>
          </Space>
          <Table
            rowKey="id"
            dataSource={domains}
            columns={domainColumns}
            loading={loadingDomains}
            locale={{ emptyText: <Empty description="暂无授权域名" /> }}
            size="middle"
            scroll={{ x: 900 }}
          />
        </Card>
      )
    },
    {
      key: 'users',
      label: '下属用户',
      children: (
        <Card variant="borderless" className="shadow-sm">
          <Space style={{ marginBottom: 'var(--spacing-md)' }}>
            <Button icon={<ReloadOutlined />} onClick={() => loadUsers()} loading={usersState.loading}>
              刷新
            </Button>
            <Text type="secondary">共 {usersState.total} 名用户</Text>
          </Space>
          <Table
            rowKey="id"
            dataSource={usersState.items}
            columns={userColumns}
            loading={usersState.loading}
            size="middle"
            pagination={{
              current: usersState.page,
              pageSize: usersState.limit,
              total: usersState.total,
              showSizeChanger: true,
              onChange: (page, pageSize) => loadUsers(page, pageSize)
            }}
            locale={{ emptyText: <Empty description="暂无下属用户" /> }}
            scroll={{ x: 800 }}
          />
        </Card>
      )
    },
    {
      key: 'generations',
      label: '生成记录',
      children: (
        <Card variant="borderless" className="shadow-sm">
          <Space className="mb-3" wrap>
            <Button icon={<ReloadOutlined />} onClick={() => loadGenerations()} loading={generationsState.loading}>
              刷新
            </Button>
            <Select<'all' | 'image' | 'video'>
              value={generationsType}
              onChange={(value) => setGenerationsType(value)}
              style={{ width: 160 }}
            >
              <Select.Option value="all">全部类型</Select.Option>
              <Select.Option value="image">图片生成</Select.Option>
              <Select.Option value="video">视频生成</Select.Option>
            </Select>
            <Text type="secondary">共 {generationsState.total} 条记录</Text>
          </Space>
          <Table
            rowKey="id"
            dataSource={generationsState.items}
            columns={generationColumns}
            loading={generationsState.loading}
            size="middle"
            pagination={{
              current: generationsState.page,
              pageSize: generationsState.limit,
              total: generationsState.total,
              showSizeChanger: true,
              onChange: (page, pageSize) => loadGenerations(page, pageSize, generationsType)
            }}
            locale={{ emptyText: <Empty description="暂无生成记录" /> }}
            scroll={{ x: 900 }}
          />
        </Card>
      )
    },
    {
      key: 'payments',
      label: '充值记录',
      children: (
        <Card variant="borderless" className="shadow-sm">
          <Space style={{ marginBottom: 'var(--spacing-md)' }}>
            <Button icon={<ReloadOutlined />} onClick={() => loadPayments()} loading={paymentsState.loading}>
              刷新
            </Button>
            <Text type="secondary">共 {paymentsState.total} 笔充值</Text>
          </Space>
          <Table
            rowKey="id"
            dataSource={paymentsState.items}
            columns={paymentColumns}
            loading={paymentsState.loading}
            size="middle"
            pagination={{
              current: paymentsState.page,
              pageSize: paymentsState.limit,
              total: paymentsState.total,
              showSizeChanger: true,
              onChange: (page, pageSize) => loadPayments(page, pageSize)
            }}
            locale={{ emptyText: <Empty description="暂无充值记录" /> }}
            scroll={{ x: 900 }}
          />
        </Card>
      )
    },
    {
      key: 'points',
      label: '积分日志',
      children: (
        <Card variant="borderless" className="shadow-sm">
          <Space style={{ marginBottom: 'var(--spacing-md)' }}>
            <Button icon={<ReloadOutlined />} onClick={() => loadPointsLogs()} loading={pointsState.loading}>
              刷新
            </Button>
            <Text type="secondary">共 {pointsState.total} 条日志</Text>
          </Space>
          <Table
            rowKey="id"
            dataSource={pointsState.items}
            columns={pointsColumns}
            loading={pointsState.loading}
            size="middle"
            pagination={{
              current: pointsState.page,
              pageSize: pointsState.limit,
              total: pointsState.total,
              showSizeChanger: true,
              onChange: (page, pageSize) => loadPointsLogs(page, pageSize)
            }}
            locale={{ emptyText: <Empty description="暂无积分日志" /> }}
            scroll={{ x: 900 }}
          />
        </Card>
      )
    }
  ]

  return (
    <div className="unified-container relative z-10" style={{
        paddingTop: 'var(--spacing-sm)'
      }}>
      <Space className="align-center" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <Title level={3} style={{ margin: 0 }}>
          代理商门户
        </Title>
        <Button icon={<ReloadOutlined />} onClick={loadOverview} loading={loadingOverview}>
          刷新概览
        </Button>
      </Space>

      {loadingOverview && !overview ? (
        <div className="py-10 text-center">
          <Spin />
        </div>
      ) : overview ? (
        <Space direction="vertical" size={20} className="w-full">
          {overviewCards}
        </Space>
      ) : (
        <Card className="shadow-sm">
          <Empty description="暂未获取到代理商信息" />
        </Card>
      )}

      <Card variant="borderless" className="shadow-sm mt-6">
        <Tabs
          type="card"
          size="large"
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          items={tabsItems}
          destroyOnHidden={false}
        />
      </Card>

      <Modal
        open={previewModal.visible}
        onCancel={() => setPreviewModal((prev) => ({ ...prev, visible: false }))}
        width="70vw"
        centered
        destroyOnHidden
        title={
          <div className="flex items-center justify-between">
            <div className="text-lg font-semibold">
              {previewModal.prompt || (previewModal.type === 'video' ? '视频预览' : '图片预览')}
            </div>
            <Space>
              <Button onClick={() => setPreviewModal((prev) => ({ ...prev, visible: false }))}>
                关闭
              </Button>
              <Button
                type="primary"
                loading={downloadLoading}
                onClick={handleDownload}
              >
                下载
              </Button>
            </Space>
          </div>
        }
      >
        <div className="text-center">
          {previewModal.type === 'video' && previewModal.url ? (
            <video
              key={previewModal.url}
              controls
              style={{ width: '100%', maxHeight: '65vh', borderRadius: 12, background: '#000' }}
              src={previewModal.url}
            >
              您的浏览器不支持视频播放。
            </video>
          ) : previewModal.urls.length > 0 ? (
            <Space direction="vertical" size={16} className="w-full">
              {previewModal.urls.map((url, idx) => (
                <img
                  key={`${url}-${idx}`}
                  src={url}
                  alt={`预览 ${idx + 1}`}
                  style={{
                    width: '100%',
                    maxHeight: '65vh',
                    objectFit: 'contain',
                    borderRadius: 12,
                    background: '#f8fafc',
                    padding: 8
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              ))}
            </Space>
          ) : (
            <Empty description="暂无可预览的文件" />
          )}
        </div>
      </Modal>
    </div>
  )
}

export default AgentPortalPage


